import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button, Card, CardBody, Col, Container, Form, FormFeedback, FormGroup,
  Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table
} from "reactstrap";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { FaCogs, FaPlus, FaSearch, FaTrash, FaEdit, FaListOl } from "react-icons/fa";
import { commissionPlanActions } from "../../sagas/commissionPlanSlice";
import { formatPrice } from "../../helpers/utility";

const PERIOD_OPTIONS = [
  { value: "DAYS_15", label: "15 jours" },
  { value: "DAYS_30", label: "30 jours" },
  { value: "DAYS_45", label: "45 jours" },
  { value: "DAYS_60", label: "60 jours" },
  { value: "DAYS_90", label: "90 jours" },
];

const BASE_OPTIONS = [
  { value: "ON_REVENUE", label: "Chiffre d'affaires (HT)" },
  { value: "ON_MARGIN",  label: "Marge (HT)" },
];

const emptyPlan = {
  id: null,
  period: "DAYS_30",
  baseType: "ON_REVENUE",
  objectiveAmount: 0,
  baseRate: 0,      // ex: 0.05 = 5%
  bonusRate: 0,     // ex: 0.02 = 2%
  progressiveTiersEnabled: false,
  tiers: [],        // [{threshold, rate}]
};

const schema = Yup.object({
  period: Yup.string().oneOf(PERIOD_OPTIONS.map(o => o.value)).required(),
  baseType: Yup.string().oneOf(["ON_REVENUE","ON_MARGIN"]).required(),
  objectiveAmount: Yup.number().min(0, "≥ 0").required(),
  baseRate: Yup.number().min(0, "≥ 0").when("progressiveTiersEnabled", {
    is: false, then: s => s.moreThan(-1, "Obligatoire si pas de paliers"),
  }),
  bonusRate: Yup.number().min(0, "≥ 0").required(),
  progressiveTiersEnabled: Yup.boolean(),
  tiers: Yup.array().of(
    Yup.object({
      threshold: Yup.number().min(0, "≥ 0").required("Requis"),
      rate: Yup.number().min(0, "≥ 0").required("Requis"),
    })
  ).test("tiers-ascending", "Les seuils doivent être strictement croissants", (tiers, ctx) => {
    const enabled = ctx.parent.progressiveTiersEnabled;
    if (!enabled) return true;
    if (!tiers || tiers.length === 0) return ctx.createError({ message: "Ajoute au moins un palier" });
    for (let i=1;i<tiers.length;i++){
      if (!(tiers[i].threshold > tiers[i-1].threshold)) return false;
    }
    return true;
  }),
});

const CommissionPlanList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { list, loading } = useSelector((s) => s.commissionPlans || { list: [], loading: false });

  const [filters, setFilters] = useState({ query: "" });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editing, setEditing] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    dispatch(commissionPlanActions.list({ ...filters }));
  }, []);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, [list]);

  const debouncedSearch = useCallback(
    debounce((query) => {
      dispatch(commissionPlanActions.list({ query }));
    }, 800),
    []
  );
  
  const openCreate = () => {
    formik.resetForm();
    setEditing(false);
    setIsModalVisible(true);
  };

  const openEdit = (id) => {
    const plan = list.find(p => p.id === id);
    if (!plan) return;
    formik.setValues({
      id: plan.id,
      period: plan.period,
      baseType: plan.baseType,
      objectiveAmount: plan.objectiveAmount,
      baseRate: Number(plan.baseRate) * 100,
      bonusRate: Number(plan.bonusRate) * 100,
      progressiveTiersEnabled: plan.progressiveTiersEnabled,
      tiers: [...(plan.tiers || [])].map(t => ({ ...t, rate: Number(t.rate) * 100 })).sort((a,b)=> Number(a.threshold) - Number(b.threshold)),
    });
    setEditing(true);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      icon: "warning",
      title: "Supprimer ce plan ?",
      text: "Cette action est irréversible.",
      showCancelButton: true,
      confirmButtonText: "Oui",
      cancelButtonText: "Non",
    }).then((res) => {
      if (res.isConfirmed) {
        dispatch(commissionPlanActions.delete({
          id,
          onSuccess: () => Swal.fire({ icon: "success", title: "Supprimé" }),
          onError: (msg) => Swal.fire({ icon: "error", title: "Erreur", text: msg || "Suppression impossible" }),
        }));
      }
    });
  };

  const handleSortTiers = (tiers, setFieldValue) => {
    const sorted = [...tiers].sort((a,b) => Number(a.threshold) - Number(b.threshold));
    setFieldValue("tiers", sorted);
  };

  const formik = useFormik({
    initialValues: emptyPlan,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const payload = { 
        ...values, 
        baseRate: Number(values.baseRate) / 100,
        bonusRate: Number(values.bonusRate) / 100,
        tiers: values.progressiveTiersEnabled 
          ? values.tiers.map(t => ({ ...t, rate: Number(t.rate) / 100 }))
          : [] 
      };
      const action = values.id ? commissionPlanActions.update : commissionPlanActions.create;
      dispatch(action({
        data: payload,
        onSuccess: () => {
          Swal.fire({ icon: "success", title: "Succès", text: editing ? "Plan modifié" : "Plan créé" });
          setIsModalVisible(false);
          dispatch(commissionPlanActions.list({ ...filters }));
        },
        onError: (msg) => Swal.fire({ icon: "error", title: "Erreur", text: msg || "Enregistrement impossible" }),
      }));
    },
  });

  const error = (name) => formik.touched[name] && formik.errors[name];

  return (
    <Container fluid>
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <div className="mt-2 text-muted">{t('common.loading') || 'Chargement...'}</div>
        </div>
      ) : (
        <>
          <Row className="mb-4">
            <Col>
              <Card className="shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 d-flex align-items-center">
                    <FaCogs className="text-primary me-2" />
                    Plans de commission
                  </h4>
                  <div className="d-flex gap-2">
                    <Button color="primary" onClick={openCreate}>
                      <FaPlus className="me-2" /> Nouveau plan
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm">
            <CardBody>
              <Row className="mb-3 g-2 align-items-end">
                <Col lg={6}>
                  <FormGroup className="mb-0">
                    <Label className="mb-1">{t('reglement.search') || 'Recherche'}</Label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <FaSearch />
                      </span>
                      <Input
                        type="text"
                        innerRef={searchInputRef}
                        placeholder="Par période / base / objectif…"
                        value={filters.query}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFilters(f => ({ ...f, query: v }));
                          debouncedSearch(v);
                        }}
                      />
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Période</th>
                    <th>Base</th>
                    <th>Objectif</th>
                    <th>Taux base</th>
                    <th>Bonus</th>
                    <th>Paliers ?</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(list || []).map(p => (
                    <tr key={p.id}>
                      <td>{p.id}</td>
                      <td>{p.period}</td>
                      <td>
                        {p.baseType === "ON_REVENUE"
                          ? <span className="badge bg-info">CA</span>
                          : <span className="badge bg-warning text-dark">Marge</span>}
                      </td>
                      <td>{formatPrice(p.objectiveAmount)}</td>
                      <td>{(Number(p.baseRate) * 100).toFixed(2)} %</td>
                      <td>{(Number(p.bonusRate) * 100).toFixed(2)} %</td>
                      <td>{p.progressiveTiersEnabled ? <span className="badge bg-success">Oui</span> : "Non"}</td>
                      <td>
                        <Button size="sm" color="warning" onClick={() => openEdit(p.id)}><FaEdit /></Button>{" "}
                        <Button size="sm" color="danger" onClick={() => handleDelete(p.id)}><FaTrash /></Button>
                      </td>
                    </tr>
                  ))}
                  {(!list || list.length === 0) && (
                    <tr><td colSpan={8} className="text-center text-muted py-4">Aucun plan</td></tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </>
      )}

      {/* MODAL CREATE / EDIT */}
      <Modal isOpen={isModalVisible} toggle={() => setIsModalVisible(false)} size="xl">
        <ModalHeader toggle={() => setIsModalVisible(false)}>
          {editing ? `Modifier plan #${formik.values.id}` : "Nouveau plan"}
        </ModalHeader>
        <FormikProvider value={formik}>
          <ModalBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="g-3">
              <Col md={3}>
                <FormGroup>
                  <Label>Période</Label>
                  <Input
                    type="select"
                    name="period"
                    value={formik.values.period}
                    onChange={formik.handleChange}
                    invalid={!!error("period")}
                  >
                    {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Input>
                  <FormFeedback>{formik.errors.period}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Base de calcul</Label>
                  <Input
                    type="select"
                    name="baseType"
                    value={formik.values.baseType}
                    onChange={formik.handleChange}
                    invalid={!!error("baseType")}
                  >
                    {BASE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Input>
                  <FormFeedback>{formik.errors.baseType}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Objectif (HT)</Label>
                  <Input
                    type="number"
                    name="objectiveAmount"
                    value={formik.values.objectiveAmount}
                    onChange={formik.handleChange}
                    min={0}
                    step="0.01"
                    invalid={!!error("objectiveAmount")}
                    onFocus={(e)=> e.target.select()}
                  />
                  <FormFeedback>{formik.errors.objectiveAmount}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Taux base (%)</Label>
                  <Input
                    type="number"
                    name="baseRate"
                    value={formik.values.baseRate}
                    onChange={formik.handleChange}
                    min={0}
                    step="0.01"
                    disabled={formik.values.progressiveTiersEnabled}
                    invalid={!!error("baseRate")}
                    onFocus={(e)=> e.target.select()}
                  />
                  <small className="text-muted">Ex: 5 pour 5 %</small>
                  <FormFeedback>{formik.errors.baseRate}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-3">
              <Col md={3}>
                <FormGroup>
                  <Label>Taux bonus (%)</Label>
                  <Input
                    type="number"
                    name="bonusRate"
                    value={formik.values.bonusRate}
                    onChange={formik.handleChange}
                    min={0}
                    step="0.01"
                    invalid={!!error("bonusRate")}
                    onFocus={(e)=> e.target.select()}
                  />
                  <small className="text-muted">Appliqué sur la part {">"} objectif</small>
                  <FormFeedback>{formik.errors.bonusRate}</FormFeedback>
                </FormGroup>
              </Col>

              <Col md={9} className="d-flex align-items-end">
                <FormGroup check>
                  <Input
                    type="checkbox"
                    name="progressiveTiersEnabled"
                    checked={formik.values.progressiveTiersEnabled}
                    onChange={(e)=> formik.setFieldValue("progressiveTiersEnabled", e.target.checked)}
                  />{" "}
                  <Label check className="ms-2">Activer les paliers progressifs</Label>
                </FormGroup>
              </Col>
            </Row>

            {/* TIERS */}
            {formik.values.progressiveTiersEnabled && (
              <Card className="mt-3">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0"><FaListOl className="me-2" />Paliers (jusqu’à l’objectif)</h6>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        color="secondary"
                        onClick={() => handleSortTiers(formik.values.tiers, formik.setFieldValue)}
                      >Trier par seuil</Button>
                      <Button
                        size="sm"
                        color="success"
                        onClick={() => formik.setFieldValue("tiers", [...(formik.values.tiers||[]), { threshold: 0, rate: 0 }])}
                      ><FaPlus className="me-1" />Ajouter palier</Button>
                    </div>
                  </div>

                  <FieldArray
                    name="tiers"
                    render={({ remove }) => (
                      <Table bordered size="sm" responsive>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Seuil cumulé (HT)</th>
                            <th>Taux (%)</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formik.values.tiers || []).map((tier, idx) => (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td style={{ maxWidth: 220 }}>
                                <Input
                                  type="number"
                                  name={`tiers[${idx}].threshold`}
                                  value={tier.threshold}
                                  min={0}
                                  step="0.01"
                                  onChange={formik.handleChange}
                                  invalid={!!(formik.touched.tiers && formik.errors.tiers)}
                                  onFocus={(e)=> e.target.select()}
                                />
                              </td>
                              <td style={{ maxWidth: 160 }}>
                                <Input
                                  type="number"
                                  name={`tiers[${idx}].rate`}
                                  value={tier.rate}
                                  min={0}
                                  step="0.01"
                                  onChange={formik.handleChange}
                                  invalid={!!(formik.touched.tiers && formik.errors.tiers)}
                                  onFocus={(e)=> e.target.select()}
                                />
                                <small className="text-muted">Ex: 7 pour 7 %</small>
                              </td>
                              <td className="text-center" style={{ width: 80 }}>
                                <Button size="sm" color="danger" onClick={() => remove(idx)}>
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {(!formik.values.tiers || formik.values.tiers.length === 0) && (
                            <tr><td colSpan={4} className="text-center text-muted">Aucun palier</td></tr>
                          )}
                        </tbody>
                      </Table>
                    )}
                  />
                  {formik.errors.tiers && typeof formik.errors.tiers === "string" && (
                    <div className="text-danger small mt-1">{formik.errors.tiers}</div>
                  )}
                </CardBody>
              </Card>
            )}
          </Form>
        </ModalBody>
        </FormikProvider>
        <ModalFooter>
          <Button color="primary" onClick={formik.submitForm}>
            {editing ? "Enregistrer" : "Créer"}
          </Button>
          <Button color="secondary" onClick={() => setIsModalVisible(false)}>
            {t('common.cancel') || 'Annuler'}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default CommissionPlanList;
